// Pure-SVG sparkline for KPI overview cards. 100×36 viewBox, stroke 1.5,
// end-point dot 2px. Colour is driven by the parent wrapper's text colour
// (Tailwind `text-emerald-500` etc.) so the inline `currentColor` picks up
// RAG semantics without ever setting hex values in the SVG.
//
// Pattern rule: a data-level chart is a labelled SVG (role="img" +
// aria-label) — this component; a chrome-level trend is a decorative
// sparkline (aria-hidden) + adjacent text counterpart — see
// components/common/Sparkline.tsx for that counterpart.

import type { JSX } from "react";

export type SparklineRag = "green" | "amber" | "red" | "neutral";

export interface KpiSparklineProps {
  points: number[];
  rag: SparklineRag;
  ariaLabel: string;
}

const WIDTH = 100;
const HEIGHT = 36;
const MARGIN_Y = 4;

const RAG_CLASS: Record<SparklineRag, string> = {
  green: "text-emerald-500 dark:text-emerald-400",
  amber: "text-amber-500 dark:text-amber-400",
  red: "text-red-500 dark:text-red-400",
  neutral: "text-slate-500 dark:text-slate-400",
};

function scaleY(value: number, min: number, max: number): number {
  if (max === min) return HEIGHT / 2;
  const ratio = (value - min) / (max - min);
  return HEIGHT - MARGIN_Y - ratio * (HEIGHT - MARGIN_Y * 2);
}

export default function KpiSparkline({
  points,
  rag,
  ariaLabel,
}: KpiSparklineProps): JSX.Element | null {
  if (points.length === 0) return null;

  const min = Math.min(...points);
  const max = Math.max(...points);

  const coords = points.map((y, i) => {
    const x =
      points.length === 1 ? WIDTH - 2 : (i / (points.length - 1)) * WIDTH;
    return { x, y: scaleY(y, min, max) };
  });

  const last = coords[coords.length - 1];
  const polyline = coords
    .map((c) => `${c.x.toFixed(2)},${c.y.toFixed(2)}`)
    .join(" ");

  return (
    <span className={`inline-block leading-none ${RAG_CLASS[rag]}`}>
      <svg
        width={WIDTH}
        height={HEIGHT}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={ariaLabel}
      >
        {coords.length > 1 && (
          <polyline
            points={polyline}
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        )}
        <circle cx={last.x} cy={last.y} r={2} fill="currentColor" />
      </svg>
    </span>
  );
}
